'use client'

import { type ReactNode, useCallback, useMemo, useReducer } from 'react'

import {
  BulkSelectionContext,
  type BulkSelectionContextValue,
} from './BulkSelectionContext'

type Action =
  | { type: 'TOGGLE'; id: string }
  | { type: 'CLEAR' }
  | { type: 'SELECT_ALL'; ids: string[] }

const reducer = (state: Set<string>, action: Action): Set<string> => {
  switch (action.type) {
    case 'TOGGLE': {
      const next = new Set(state)
      if (next.has(action.id)) {
        next.delete(action.id)
      } else {
        next.add(action.id)
      }
      return next
    }
    case 'CLEAR':
      return new Set()
    case 'SELECT_ALL':
      return new Set(action.ids)
  }
}

export const BulkSelectionProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [selectedIds, dispatch] = useReducer(reducer, new Set<string>())

  const toggle = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE', id }),
    [dispatch]
  )
  const clear = useCallback(() => dispatch({ type: 'CLEAR' }), [dispatch])
  const selectAll = useCallback(
    (ids: string[]) => dispatch({ type: 'SELECT_ALL', ids }),
    [dispatch]
  )

  const value = useMemo<BulkSelectionContextValue>(
    () => ({ selectedIds, toggle, clear, selectAll, count: selectedIds.size }),
    [selectedIds, toggle, clear, selectAll]
  )

  return (
    <BulkSelectionContext.Provider value={value}>
      {children}
    </BulkSelectionContext.Provider>
  )
}
